import {
    ChangeDetectorRef,
    Component,
    HostBinding,
    OnDestroy, OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { IAppConfig } from "../common/app-config/common.types";
import { AssetService } from "../common/services/asset.service";
import { takeUntilDestroy } from "take-until-destroy";
import { DomSanitizer, SafeStyle, Title } from "@angular/platform-browser";
import { PopupWindowService } from "../common/services/popup.window.service";
import { ShowHideApplicationHeaderService } from "../common/services/show.hide.application.header.service";
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { Location } from '@angular/common';
import { filter } from "rxjs/operators";
import { Observable, zip } from "rxjs";
import { LABEL_SUFFIX, TITLE_SUFFIX } from "../common/translate/translate.suffix";
import { TranslateService } from "../common/translate/services/translate.service";
import { SelfSchedulingDataService } from './self.scheduling/services';

@Component({
    selector: 'app-main',
    templateUrl: './app-main.component.html',
    styleUrls: ['./app-main.component.scss']
})
export class AppMainComponent implements OnDestroy, OnInit {
    @ViewChild("notificationContainer", { read: ViewContainerRef, static: true }) public notificationContainerRef: ViewContainerRef;
    @ViewChild('closePopupContent', { static: false }) closePopupContent: TemplateRef<any>;
    @ViewChild('closePopupTitle', { static: false }) closePopupTitle: TemplateRef<any>;

    public static readonly APPLICATION_TITLE_PREFIX: string = "PatientAssistant" + TITLE_SUFFIX;

    title = 'patient-portal';
    config: IAppConfig;

    @HostBinding("style.background-image") public background: SafeStyle;
    public logo: SafeStyle;
    private favicon: HTMLElement = document.getElementById("app-icon");
    public isPopupOpen: boolean = false;
    public licenseValidated = false;
    public currentTheme: "custom" | "factory" = "custom";

    constructor(private assetService: AssetService, private sanitizer: DomSanitizer, private changeDetector: ChangeDetectorRef,
                private location: Location, private router: Router, private route: ActivatedRoute,
                public popupWindowService: PopupWindowService, public showHideApplicationHeaderService: ShowHideApplicationHeaderService,
                private translateService: TranslateService, private titleService: Title,
                private selfSchedulingDataService: SelfSchedulingDataService) {
        this.assetService.getStaticAssetByCode("SITE_PORTAL_LOGO_MOBILE").pipe(takeUntilDestroy(this)).subscribe((res) => {
            this.logo = this.sanitizer.bypassSecurityTrustResourceUrl(res);
            this.changeDetector.markForCheck();
        });

        // Safari iPhone pull-to-refresh prevention
        const isSafariIPhone = /iPhone/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);

        if (isSafariIPhone) {
            window.addEventListener('beforeunload', (event) => {
                event.preventDefault();
                event.returnValue = '';

                return '';
            });

            let startY = 0;
            let preventPullToRefresh = false;

            document.addEventListener('touchstart', (e) => {
                if (e.touches.length !== 1) return;
                startY = e.touches[0].clientY;

                // Find the scrollable ancestor
                let el = e.target as HTMLElement;
                preventPullToRefresh = true;
                while (el && el !== document.body) {
                    const style = window.getComputedStyle(el);
                    const overflowY = style.overflowY;
                    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                        // If the scrollable element is not at its top, don't prevent
                        if (el.scrollTop > 0) {
                            preventPullToRefresh = false;
                            break;
                        }
                    }
                    el = el.parentElement;
                }
                // If no scrollable ancestor found, fallback to window scroll
                if (preventPullToRefresh) {
                    preventPullToRefresh = window.scrollY === 0;
                }
            }, { passive: false });

            document.addEventListener('touchmove', (e) => {
                const pullDelta = e.touches[0].clientY - startY;
                if (preventPullToRefresh && pullDelta > 0) {
                    e.preventDefault();
                }
            }, { passive: false });

            document.addEventListener('touchend', () => {
                preventPullToRefresh = false;
            }, { passive: false });

            const style = document.createElement('style');
            style.textContent = 'html,body{overscroll-behavior:none;-webkit-overflow-scrolling:touch}body{overflow-x:hidden}';
            document.head.appendChild(style);
        }

        //When navigation comes from the browser back/forward buttons, cancel it:
        router.events.pipe(
            filter(event => event instanceof NavigationStart && event.navigationTrigger === "popstate"),
            takeUntilDestroy(this)
        ).subscribe((event: NavigationStart) => {
            // Cancel navigation:
            this.router.navigateByUrl(this.router.url);
            this.location.go(this.router.url);

            // Show navigation confirmation popup
            this.showNavigationConfirmationPopup();
        });

        // Check if the device passes the size check for mobile styling and load the background image only if it's not a mobile device
        if (!window.matchMedia("(max-device-height: 950px) and (max-device-aspect-ratio: 1/1), (max-device-width: 950px) and (min-device-aspect-ratio: 1/1)").matches) {
            this.assetService.getStaticAssetByCode("SITE_PORTAL_LOGO_BACKGROUND").pipe(takeUntilDestroy(this)).subscribe((res) => {
                this.background = this.sanitizer.bypassSecurityTrustStyle("url(" + res + ")");
                this.changeDetector.markForCheck();
            });
        }

        this.assetService.getStaticAssetByCode("SITE_PORTAL_LOGO_FAVICON").pipe(takeUntilDestroy(this)).subscribe((res) => {
            this.favicon.setAttribute("href", res);
            this.changeDetector.markForCheck();
        });
    }

    ngOnDestroy(): void {

    }

    ngOnInit(): void {
        // Change application title after route changes
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((e) => {
            const applicationTitle = this.translateService.transform(AppMainComponent.APPLICATION_TITLE_PREFIX, []);
            let node = this.route.snapshot;
            let titleEntries: Observable<string>[] = [applicationTitle];

            while (!!node) {
                if (node.data?.title) {
                    const titleToTranslate: string = node.data?.title + TITLE_SUFFIX;
                    const title: Observable<string> = this.translateService.transform(titleToTranslate, []);
                    titleEntries.push(title);
                }

                if (node.data?.breadcrumbs) {
                    const breadcrumbLabelToTranslate: string = node.data.breadcrumbs.find((breadcrumb) => breadcrumb.url === node?.firstChild.routeConfig.path)?.title + LABEL_SUFFIX;
                    const breadcrumb: Observable<string> = this.translateService.transform(breadcrumbLabelToTranslate, []);
                    titleEntries.push(breadcrumb);
                }

                node = node.firstChild;
            }

            zip(...titleEntries).pipe(takeUntilDestroy(this)).subscribe((titles: string[]) => {
                const title: string = titles.join(" - ");
                this.titleService.setTitle(title);

                // console.log("Set title from route", title);
            });
        });

        this.selfSchedulingDataService.clearData(false);
        this.selfSchedulingDataService.notificationContainerRef = this.notificationContainerRef;
        
        this.selfSchedulingDataService.navigationConfirmationPopupSubject.pipe(takeUntilDestroy(this)).subscribe(() => {
            this.showNavigationConfirmationPopup();
            this.changeDetector.markForCheck();
        });
    }

    private showNavigationConfirmationPopup() {
        if (!this.isPopupOpen) {
            this.isPopupOpen = true;
            this.popupWindowService.openPopupWindow(this.closePopupContent, this.closePopupTitle, "close-popup-title", window.outerWidth - 20, 190).pipe(takeUntilDestroy(this))
                .subscribe((result) => {
                    if (result === true) {
                        //navigate to login:
                        this.router.navigate(['/login'], { replaceUrl: true });
                    }
                    this.isPopupOpen = false;
                });
        }
    }
}
